import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { getNextPayment } from '@/store/actions/paymentActions';
import Preloader from '@/components/UI/Preloader';
import { RoundLink } from '@/components/UI/RoundLink';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { RefinBanner } from '@/components/RefinBanner';
import { APP_LINKS } from '@/helpers/links';
import { ShiftPaymentModal } from '@/modals/ShiftPaymentModal';
import { useGetLoansList } from '@/query/queries/loan/useGetLoansList';
import { ROLES } from '@/helpers/constants';
import { ApplyRefinModal } from '../../components/ApplyRefinModal';
import { LoanCard } from '../../components/LoanCard';
import s from './index.module.scss';

export const CombinedView = () => {
  const { t } = useTranslation('loans');
  const [modal, setModal] = useState(null);
  const dispatch = useDispatch();
  const { user } = useGetMe();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const [searchParams] = useSearchParams();
  const fundraising = searchParams.get('fundraising');

  const { data, isFetching } = useGetLoansList(params);

  const { nextPayment } = useSelector(state => state.paymentStore);

  useEffect(() => {
    dispatch(getNextPayment());
  }, []);

  const onLoanOpen = useCallback(loan => {
    dispatch(
      setModals({
        type: MODAL_TYPES.loan_info,
        selectedLoan: loan,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (fundraising && !isFetching && data?.list?.length) {
      const searchLoan = data.list.find(i => i.fundraising_id === Number(fundraising));
      if (searchLoan) {
        onLoanOpen(searchLoan);
      }
    }
  }, [data, fundraising, isFetching, onLoanOpen]);

  const onRefinInfoClick = () => {
    setModal({ type: 'about' });
  };

  const hasNotPaidLoans = !nextPayment.loading && !!nextPayment.data;
  const isEmpty = !(data && data.list.length);

  const { current, history } = useMemo(() => {
    if (!data) return { current: [], history: [] };

    return data.list.reduce(
      (acc, item) => {
        item.payment_progress >= 100 ? acc.history.push(item) : acc.current.push(item);
        return acc;
      },
      { current: [], history: [] },
    );
  }, [data]);

  return (
    <div className={s.page}>
      <ShiftPaymentModal />
      <div className="container-inner">
        {isFetching ? (
          <Preloader style={{ height: '30vh' }} />
        ) : (
          <div className={s.content}>
            {!!current.length && (
              <>
                <div className={s.header}>
                  <h1 className={classnames(s.page__title, 'f-500', 'f-42')}>
                    {t('loans_page.title')}
                  </h1>
                  {(!isEmpty || hasNotPaidLoans) && (
                    <div className={s.page__links}>
                      <RoundLink
                        fillBackground
                        label={t('loans_page.next_repayment')}
                        path={APP_LINKS.nextPayment}
                      />
                    </div>
                  )}
                </div>
                <RefinBanner onMore={onRefinInfoClick} className={s.banner} />
                {isEmpty ? (
                  <div className={classnames(s.page__empty, 'f-16')}>
                    {t('loans_page.no_loans')}
                  </div>
                ) : (
                  <div className={s.cards}>
                    {current.map(loan => (
                      <LoanCard
                        key={loan.fundraising_id}
                        loan={loan}
                        canApply={user.role === ROLES.founder}
                        onClick={() => onLoanOpen(loan)}
                        onApply={() => setModal({ type: 'apply', data: loan })}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {!!history.length && (
              <>
                <div className={s.header}>
                  <h1 className={classnames(s.page__title, 'f-500', 'f-42')}>
                    {t('loans_page.history')}
                  </h1>
                </div>
                {isEmpty ? (
                  <div className={classnames(s.page__empty, 'f-16')}>
                    {t('loans_page.no_loans')}
                  </div>
                ) : (
                  <div className={s.cards}>
                    {history.map(loan => (
                      <LoanCard
                        key={loan.fundraising_id}
                        loan={loan}
                        canApply={user.role === ROLES.founder}
                        onClick={() => onLoanOpen(loan)}
                        onApply={() => setModal({ type: 'apply', data: loan })}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {modal?.type === 'apply' && (
        <ApplyRefinModal loan={modal.data} onClose={() => setModal(null)} />
      )}
    </div>
  );
};